import { Button } from "antd";
import type { ServerErrorMessage } from "types/api_types";
import Toast, { showToastOnce } from "./toast";

let _pingFn: (str: string) => void = () => {
  console.warn(
    "pingMentionedDataStores was called before registerPingFn — datastore health check skipped",
  );
};

export function registerPingFn(fn: (str: string) => void): void {
  _pingFn = fn;
}

export const handleError = async (
  requestedUrl: string,
  showErrorToast: boolean,
  doInvestigate: boolean,
  error: Response | Error,
): Promise<void> => {
  if (doInvestigate) {
    // Check whether this request failed due to a problematic datastore
    _pingFn(requestedUrl);
    if (error instanceof Response) {
      // Handle 401 Unauthorized errors and ensure an understandable error toast is shown.
      // This might happen e.g. after a user logged out everywhere.
      if (error.status === 401 && showErrorToast) {
        showToastOnce(
          "error",
          <>
            Your session has expired. Please refresh the page
            <br />
            <Button type="default" onClick={() => location.reload()}>
              Refresh
            </Button>
          </>,
          { timeout: 10000, sticky: true },
        );
      }
      return error.text().then(
        (text) => {
          try {
            const json = JSON.parse(text);

            // Propagate HTTP status code for further processing down the road
            if (error.status != null) {
              json.status = error.status;
            }

            const messages = json.messages.map((message: ServerErrorMessage[]) => ({
              ...message,
              key: json.status.toString(),
            }));
            if (showErrorToast) {
              Toast.messages(messages); // Note: Toast.error internally logs to console
            } else {
              console.error(messages);
            }
            // Check whether the error chain mentions an url which belongs
            // to a datastore. Then, ping the datastore
            _pingFn(text);

            /* eslint-disable-next-line prefer-promise-reject-errors */
            return Promise.reject({ ...json, url: requestedUrl });
          } catch (_jsonError) {
            if (showErrorToast) {
              Toast.error(text); // Note: Toast.error internally logs to console
            } else {
              console.error(`Request failed for ${requestedUrl}:`, text);
            }

            /* eslint-disable-next-line prefer-promise-reject-errors */
            return Promise.reject({
              errors: [text],
              status: error.status != null ? error.status : -1,
              url: requestedUrl,
            });
          }
        },
        (textError) => {
          Toast.error(textError.toString());
          return Promise.reject(textError);
        },
      );
    }
  }

  // If doInvestigate is false but the error is still a Response, read the body
  // so callers get a proper Error with the status and body text instead of a
  // raw Response object (whose body is a ReadableStream that can't be inspected).
  if (error instanceof Response) {
    const text = await error.text().catch(() => "<unreadable body>");
    return Promise.reject(
      new Error(`Request failed with status ${error.status} for ${requestedUrl}: ${text}`),
    );
  }

  error.message += ` - Url: ${requestedUrl}`;
  return Promise.reject(error);
};
